/**
 * Single source of truth for every route path in the portal.
 *
 * Components link through these builders rather than string literals, so a route
 * rename is a one-file change and `Link to={...}` stays type-safe.
 *
 * NOTE ON THE PROJECT IDENTIFIER (FIX-PROJECT-DETAIL-1)
 * `:projectId` is the project's UUID. The API addresses every project route by id
 * (`apps/api/src/projects/projects.controller.ts`, `ParseUUIDPipe`) and answers a
 * slug with `400`, which is what broke "View details" when the card linked by slug.
 */

export const ROUTES = {
  login: '/login',

  projects: '/projects',
  projectDetail: (projectId: string) => `/projects/${encodeURIComponent(projectId)}`,
  accessLogs: '/access-logs',
  profile: '/profile',

  admin: {
    index: '/admin',
    projects: '/admin/projects',
    projectCreate: '/admin/projects/create',
    projectDetail: (projectId: string) => `/admin/projects/${encodeURIComponent(projectId)}`,
    projectEdit: (projectId: string) => `/admin/projects/${encodeURIComponent(projectId)}/edit`,
    users: '/admin/users',
    userCreate: '/admin/users/create',
    userDetail: (userId: string) => `/admin/users/${encodeURIComponent(userId)}`,
    userEdit: (userId: string) => `/admin/users/${encodeURIComponent(userId)}/edit`,
    permissions: '/admin/permissions',
    /** One user's projects, or one project's users, on the permissions screen. */
    permissionsFor: (focus: PermissionsFocus) => {
      const params = new URLSearchParams({ view: focus.view });

      if (focus.id !== undefined) {
        params.set(focus.view, focus.id);
      }

      return `/admin/permissions?${params.toString()}`;
    },
    accessLogs: '/admin/access-logs',
    analytics: '/admin/analytics',
  },
} as const;

export type PermissionsView = 'user' | 'project';

export interface PermissionsFocus {
  readonly view: PermissionsView;
  /** A user id for the `user` view, a project id for the `project` view. */
  readonly id?: string;
}

/** Route that unauthenticated users are sent back to after signing in. */
export const DEFAULT_AUTHENTICATED_ROUTE = ROUTES.projects;
